/**
 * Weekly Agenda Grid Component
 *
 * Renders the full week grid: column headers + 7 day rows.
 * Reuses FamilyColumnHeader and useFamilyColumnPagination.
 * Exposes scrollToDay via imperative handle for programmatic scrolling.
 *
 * Follows single responsibility: weekly grid layout only.
 */

import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { SharedValue } from 'react-native-reanimated';
import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { TAB_BAR_HEIGHT } from '@/components/navigation/CustomTabBar';
import { useFamilyColumnPagination } from '@/hooks/use-family-column-pagination';
import { FamilyColumnHeader } from './FamilyColumnHeader';
import { PaginationArrowWithPreview } from './PaginationArrowWithPreview';
import { WeeklyAgendaDayRow } from './WeeklyAgendaDayRow';
import type { FamilyMemberResource } from '@/types/family-resource';
import type { WeeklyAgendaWeekData } from '@/types/weekly-agenda';
import type { CalendarEvent } from '@/types/events';

const DAY_LABEL_WIDTH = 65;

export type WeeklyAgendaGridHandle = {
  scrollToDay: (dateKey: string) => void;
};

type WeeklyAgendaGridProps = {
  weekData: WeeklyAgendaWeekData;
  resources: FamilyMemberResource[];
  onDayPress: (dateKey: string) => void;
  onEventPress?: (event: CalendarEvent) => void;
  onAvatarPress?: (resource: FamilyMemberResource) => void;
  onCreateEvent?: (dateKey: string, memberId: string) => void;
  translateX: SharedValue<number>;
  initialScrollDateKey?: string;
  scrollToTodayTrigger?: number;
  selectedDateKey?: string;
};

export const WeeklyAgendaGrid = React.memo(
  React.forwardRef<WeeklyAgendaGridHandle, WeeklyAgendaGridProps>(
    function WeeklyAgendaGrid(
      {
        weekData,
        resources,
        onDayPress,
        onEventPress,
        onAvatarPress,
        onCreateEvent,
        translateX,
        initialScrollDateKey,
        scrollToTodayTrigger,
        selectedDateKey,
      },
      ref
    ) {
      const colorScheme = useColorScheme();
      const palette = Colors[colorScheme ?? 'light'];
      const { bottom: bottomInset } = useSafeAreaInsets();
      const scrollViewRef = useRef<ScrollView>(null);
      const dayRowPositionsRef = useRef<Map<string, number>>(new Map());

      const scrollBottomPadding = useMemo(
        () => Math.max(bottomInset + TAB_BAR_HEIGHT + 16, 100),
        [bottomInset]
      );

      const {
        visibleResources,
        columnWidth,
        showPagination,
        goToNextPage,
        goToPrevPage,
        canGoNext,
        canGoPrev,
        nextPreviewResource,
        prevPreviewResource,
      } = useFamilyColumnPagination({
        resources,
        timeColumnWidth: DAY_LABEL_WIDTH,
      });

      const scrollToDay = useCallback((dateKey: string) => {
        const yPosition = dayRowPositionsRef.current.get(dateKey);
        if (yPosition != null && scrollViewRef.current) {
          scrollViewRef.current.scrollTo({ y: yPosition, animated: true });
        }
      }, []);

      useImperativeHandle(ref, () => ({ scrollToDay }), [scrollToDay]);

      const handleDayRowLayout = useCallback((dateKey: string, y: number) => {
        dayRowPositionsRef.current.set(dateKey, y);
      }, []);

      // Auto-scroll on initial render when initialScrollDateKey is set
      useEffect(() => {
        if (!initialScrollDateKey) return;
        const timer = setTimeout(() => scrollToDay(initialScrollDateKey), 100);
        return () => clearTimeout(timer);
      }, [initialScrollDateKey, scrollToDay]);

      // Respond to external scroll-to-today triggers
      useEffect(() => {
        if (!scrollToTodayTrigger) return;
        const todayKey = new Date().toISOString().split('T')[0];
        const timer = setTimeout(() => scrollToDay(todayKey), 100);
        return () => clearTimeout(timer);
      }, [scrollToTodayTrigger, scrollToDay]);

      return (
        <View style={[styles.container, { backgroundColor: palette.background }]}>
          {/* Sticky column headers row — visually continues the rounded
              header zone started by WeeklyAgendaHeader (bareContainer) and
              carries the rounded bottom + shadow that separates the zone
              from the scrolling day rows below. */}
          <View style={[styles.headerRow, { backgroundColor: palette.cardBackground }]}>
            {/* Left pagination arrow */}
            <View style={[styles.dayLabelSpacer, { borderRightColor: palette.border }]}>
              {showPagination && (
                <PaginationArrowWithPreview
                  direction="left"
                  onPress={goToPrevPage}
                  disabled={!canGoPrev}
                  previewMember={prevPreviewResource}
                />
              )}
            </View>

            {/* Family member column headers */}
            <FamilyColumnHeader
              visibleResources={visibleResources}
              columnWidth={columnWidth}
              textColor={palette.text}
              onAvatarPress={onAvatarPress}
            />

            {/* Right pagination arrow */}
            {showPagination && (
              <PaginationArrowWithPreview
                direction="right"
                onPress={goToNextPage}
                disabled={!canGoNext}
                previewMember={nextPreviewResource}
              />
            )}
          </View>

          {/* Spacer so the header's shadow has room to breathe before the day rows */}
          <View style={styles.headerShadowSpacer} />

          {/* Scrollable day rows */}
          <ScrollView ref={scrollViewRef} style={styles.scrollView} showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: scrollBottomPadding }}>
            {weekData.days.map(dayRow => (
              <View
                key={dayRow.dateKey}
                onLayout={(e) => handleDayRowLayout(dayRow.dateKey, e.nativeEvent.layout.y)}
              >
                <WeeklyAgendaDayRow
                  dayRow={dayRow}
                  visibleResources={visibleResources}
                  columnWidth={columnWidth}
                  onDayPress={onDayPress}
                  onEventPress={onEventPress}
                  onCreateEvent={onCreateEvent}
                  translateX={translateX}
                  selectedDateKey={selectedDateKey}
                />
              </View>
            ))}
          </ScrollView>
        </View>
      );
    }
  )
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  headerShadowSpacer: {
    height: 16,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.18,
        shadowRadius: 8,
      },
      android: {
        elevation: 6,
      },
      default: {},
    }),
  },
  dayLabelSpacer: {
    width: DAY_LABEL_WIDTH,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
